import React, { useCallback, useRef } from 'react';
import { Box, Group, Popper, Text, useDisclosure } from '@landr/maestro';
import { Classifier } from 'types/generated/graphql';
import { SearchableClassifier } from './SearchableClassifier';

export const PopoverClassifiers = React.memo(
    ({
        classifiers,
        isSearchEnabled = true,
    }: {
        classifiers: Classifier[];
        isSearchEnabled?: boolean;
    }) => {
        const ref = useRef(null);
        const { isOpen, onToggle, onClose } = useDisclosure();

        const handleClick = useCallback(
            (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                event?.preventDefault();
                event?.stopPropagation();

                onToggle();
            },
            [onToggle],
        );

        return (
            <Box position="relative" display="inline-block">
                <Text
                    ref={ref}
                    textStyle="body-sm"
                    color="text.medium"
                    onClick={handleClick}
                >
                    {`+${classifiers.length}`}
                </Text>
                <Popper
                    isOpen={isOpen}
                    onClickOutside={onClose}
                    anchorRef={ref}
                    popperOptions={{
                        placement: 'bottom-start',
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 10],
                                },
                            },
                        ],
                    }}
                >
                    <Box
                        p="xs"
                        backgroundColor="surface.medium"
                        borderColor="border.surface"
                        borderRadius="sm"
                        borderWidth="1px"
                        borderStyle="solid"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                        maxWidth="250px"
                    >
                        <Group gap="none" direction="vertical">
                            {classifiers?.map((classifier) => (
                                <Group.Item
                                    whiteSpace="nowrap"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    maxWidth="100%"
                                    key={classifier.code}
                                >
                                    <SearchableClassifier
                                        classifier={classifier}
                                        isSearchEnabled={isSearchEnabled}
                                        isPopover={true}
                                    />
                                </Group.Item>
                            ))}
                        </Group>
                        <Popper.Arrow />
                    </Box>
                </Popper>
            </Box>
        );
    },
    (prevProps, nextProps) => {
        // Compare each prop individually
        return (
            prevProps.isSearchEnabled === nextProps.isSearchEnabled &&
            JSON.stringify(prevProps.classifiers) ===
                JSON.stringify(nextProps.classifiers)
        );
    },
);
